import { Component, OnInit, Input, OnChanges, EventEmitter, Output } from '@angular/core';
import { FileService } from './file.service';
import { ValueAccessorBase } from '../value-accessor-base';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ILabel } from '../ILabel.model';
import { ActivatedRoute } from '@angular/router';
import { TranslationService } from '../common/services/translation.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { MessagesService } from '../common/services/messages.service';
import { AlertDialogComponent } from '../dialogs/alert-dialog/alert-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../dialogs/confirm-dialog/confirm-dialog.component';
import { downloadFile } from '../common/functions/file-helpers';
import { SelectType } from '../cpq-select/cpq-select.component';
import { ILookup } from '../common/interfaces/ILookup.interface';
import { CpqToastrService } from '../cpq-toastr/cpq-toastr.service';

@Component({
    selector: 'cpq-file-select-with-filesystem',
    templateUrl: './cpq-file-select-with-filesystem.component.html',
    styleUrls: ['./cpq-file-select-with-filesystem.component.scss'],
    providers: [FileService, {
            provide: NG_VALUE_ACCESSOR,
            useExisting: CpqFileSelectWithFileSystemComponent,
            multi: true
        }],
    standalone: false
})
export class CpqFileSelectWithFileSystemComponent extends ValueAccessorBase<string> implements OnInit, OnChanges {

  public fileList: Array<ILookup>;
  @Input('apiUrl') apiUrl: string;
  @Input('overWrite') overWrite: boolean;
  @Input('label') label: ILabel;
  @Input('type') type: FileInputType = FileInputType.SIMPLE;
  @Input('accept') accept: string;
  @Input('allowedExtensions') allowedExtensions?: Array<string>;
  @Input('isPreviewVisible') isPreviewVisible: boolean = true;
  @Input('showDelete') showDelete: boolean = true;
  @Input('multiple') multiple: boolean = false;
  @Output('onUpload') onUpload: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output('onSelect') onSelect: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output('onDownload') onDownload: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output('onRemove') onRemove: EventEmitter<boolean> = new EventEmitter<boolean>();

  public FileInputType = FileInputType;
  public previewLoaded: boolean;
  public filePath: SafeUrl;
  public allowedExtensionsInfo: string;
  public SelectType = SelectType;

  constructor(private fileService: FileService,
    public route: ActivatedRoute,
    public messagesService: MessagesService,
    public dialog: MatDialog,
    private sanitizer: DomSanitizer,
    private translateService: TranslationService,
    private toastr: CpqToastrService) {
    super();
  }

  ngOnInit() {
    this.fileService.getFileNames(this.apiUrl).subscribe(x => {
      this.fileList = this.mapToLookups(x.filter(x => this.checkIfFileExtensionIsAllowed(x)));
      if (!x.includes(this.value)) {
        this.remove();
      }

      this.preview();
    });

    this.valueObserver.subscribe(value => {
      if (value) {
        this.preview();
      } else {
        this.previewLoaded = false;
      }
    });
  }

  ngOnChanges() {
    if (this.allowedExtensions && this.allowedExtensions.length > 0) {
      this.allowedExtensionsInfo = `(${this.allowedExtensions.join(', ')})`;
    }
  }

  onFileSelected(event) {
    if (event.target.files.length > 0) {
      this.uploadFiles(event.target.files);
    }
  }

  private uploadFiles(files: any) {
    const filesCount = files.length;
    const formData = new FormData();
    let invalidFiles = new Array<string>();

    this.fileService.getFileNames(this.apiUrl).subscribe(x => {
      this.fileList = this.mapToLookups(x);
      for (const file of files) {
        const fileName = file.name;
        if (this.allowedExtensions && this.allowedExtensions.length > 0) {
          if (!this.checkIfFileExtensionIsAllowed(fileName)) {
            this.dialog.open(AlertDialogComponent, {
              data: {
                content: this.translateService.translate('Util.Fileupload.MsgSelectedFileExtensionIsForbidden') + (filesCount > 1 ? ` (${fileName})` : '')
              }
            });

            return null;
          }
        }

        let existsingFile = this.fileList.find(x => x.text === fileName);
        if (existsingFile) {
          invalidFiles.push(fileName);

          continue;
        }

        formData.append('File', file);
      }

      if (invalidFiles.length > 0) {
        this.dialog.open(AlertDialogComponent, {
          data: {
            content: this.translateService.translate('Util.Fileupload.MsgFilesAlreadyExist') + invalidFiles.join(', ')
          }
        });

        return null;
      }

      if (!formData) {
        return;
      }

      this.fileService.uploadFileChoice(this.apiUrl, formData, this.overWrite).subscribe(fileUploadResponses => {
        fileUploadResponses.forEach(response => {
          if (response.IsSuccess) {
            this.fileService.getFileNames(this.apiUrl).subscribe(x => {
              this.fileList = this.mapToLookups(x);
              this.value = files[fileUploadResponses.indexOf(response)].name;
              this.onUpload.emit(true);
              this.preview();
            });
          }
          else {
            this.messagesService.showAlertDialog({
              title: '',
              content: response.ErrorMessage
            })
          }
        })
      });

    });

  }

  checkIfFileExtensionIsAllowed(fileName) {
    const fileExtension: string = (fileName).substring(fileName.lastIndexOf('.'));
    if (this.allowedExtensions.map(extension => extension.toLocaleUpperCase()).indexOf(fileExtension.toLocaleUpperCase()) === -1) {
      return false;
    }
    return true;
  }

  download() {
    if (this.value) {
      this.fileService.downloadFile(this.apiUrl, this.value).subscribe(x => {
        downloadFile(this.value, x, '');
      });
    }

    this.onDownload.emit(true);
  }

  change() {
    this.onSelect.emit(true);
  }

  preview() {
    if (this.value && this.isPreviewVisible) {
      this.fileService.downloadFile(this.apiUrl, this.value).subscribe(x => {
        this.filePath = this.sanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(x));
        this.previewLoaded = true;
      });
    }
  }

  public changePreview(filePath: SafeUrl) {
    if (filePath && this.isPreviewVisible) {
      this.filePath = filePath;
      this.previewLoaded = true;
    }
  }

  remove() {
    this.value = '';
    this.onRemove.emit();
  }

  clearSelection() {
    this.value = '';
    this.previewLoaded = false;
    this.onRemove.emit();
  }

  delete() {
    const confirmDeleteDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.translate('Admin.Users.DeleteWarning'),
        content: `${this.translateService.translate('Admin.Users.DeleteMsg')} ${this.value}?`
      }
    });
    confirmDeleteDialog.afterClosed().subscribe(result => {
      if (result) {
        const fileName = this.value;
        this.fileService.deleteFile(this.apiUrl, fileName).subscribe(x => {
          this.fileService.getFileNames(this.apiUrl).subscribe(files => {
            this.fileList = this.mapToLookups(files);
          });
          this.toastr.success(`${fileName} ${this.translateService.translate('Admin.Users.DeleteSuccess')}`);
        });
        this.remove();
      }
    });
  }

  private mapToLookups(files: Array<string>): Array<ILookup> {
    return files.map(file => {
      const lookup = new ILookup();
      lookup.id = file;
      lookup.text = file;
      return lookup;
    });
  }
}
export enum FileInputType {
  SIMPLE = 0,
  SELECT = 1
}

